import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  ConnectionHubOpenOptions,
  ConnectionHubState,
  ConnectionSocketPayload,
} from "../api/connectionHub.types";
import {
  CONNECTION_HUB_DEFAULTS,
  REQUEST_STATUS,
} from "../constants/connectionHub.constants";
import { CONNECTION_STATUS } from "../constants/connectionStatus.constants";
import {
  fetchConnectionAccounts,
  fetchConnectionChannels,
  fetchConnectionEmailSuggestions,
} from "./connectionHub.thunks";

const emptyRequestState = () => ({
  status: REQUEST_STATUS.IDLE,
  error: null,
});

const initialState: ConnectionHubState = {
  session: {
    isOpen: false,
    source: CONNECTION_HUB_DEFAULTS.source,
    agencyClientId: CONNECTION_HUB_DEFAULTS.agencyClientId,
    mode: null,
    selectedIpIds: [],
    activeIpId: CONNECTION_HUB_DEFAULTS.activeIpId,
  },
  catalog: { data: null, ...emptyRequestState() },
  accountsByIpId: {},
  accountRequestsByIpId: {},
  emailSuggestions: { data: null, ...emptyRequestState() },
  socket: { revision: 0, latest: null },
  requestMarks: {},
};

const resetCompanyState = (state: ConnectionHubState): void => {
  state.catalog = { data: null, ...emptyRequestState() };
  state.accountsByIpId = {};
  state.accountRequestsByIpId = {};
  state.emailSuggestions = { data: null, ...emptyRequestState() };
  state.socket = { revision: state.socket.revision + 1, latest: null };
  state.requestMarks = {};
};

const sameAgency = (
  state: ConnectionHubState,
  agencyClientId?: number | null
): boolean => state.session.agencyClientId === (agencyClientId ?? null);

const applySocketPayload = (state: ConnectionHubState, payload: ConnectionSocketPayload): void => {
  const badgeByIpId = new Map(
    payload.channelBadges.map((badge) => [badge.ipId, badge])
  );

  state.catalog.data?.sections.forEach((section) => {
    section.channels.forEach((channel) => {
      const badge = badgeByIpId.get(channel.ipId);
      if (badge) {
        channel.badge = {
          status: badge.status,
          count: badge.count,
          accountCount: badge.accountCount ?? channel.badge.accountCount,
        };
        if (badge.status === CONNECTION_STATUS.CONNECTED) channel.isSuggested = false;
      }
    });
  });

  const updateById = new Map(
    payload.connections.map((connection) => [connection.id, connection])
  );
  const coverById = new Map(
    (payload.inboxGroups ?? []).flatMap((group) =>
      group.covers.map((cover) => [cover.connectionId, { cover, inbox: group.inbox }] as const)
    )
  );
  const standaloneById = new Map(
    (payload.standalone ?? []).map((card) => [card.connection.connectionId, card])
  );
  Object.values(state.accountsByIpId).forEach((accounts) => {
    accounts.forEach((account) => {
      const update = updateById.get(account.connection.connectionId);
      if (update) {
        account.status = update.status;
        account.connection.status = update.status;
        account.connection.failureReason = update.failureReason;
      }

      const groupUpdate = coverById.get(account.connection.connectionId);
      if (groupUpdate) {
        account.status = groupUpdate.cover.status;
        account.connection.status = groupUpdate.cover.status;
        account.connection.failureReason = groupUpdate.cover.failureReason;
        account.connection.otpMode = groupUpdate.cover.otpMode;
        account.connection.hasPassword = groupUpdate.cover.hasPassword;
        account.connection.pendingSubBrands = groupUpdate.cover.pendingSubBrands;
        account.connection.selectedSubBrands = groupUpdate.cover.selectedSubBrands;
        account.hasPendingSubBrands = groupUpdate.cover.pendingSubBrands.length > 0;
        account.lastError = groupUpdate.cover.lastError;
        account.fixPath = groupUpdate.cover.fixPath;
        account.inbox = {
          id: groupUpdate.inbox.id,
          email: groupUpdate.inbox.email,
          provider: groupUpdate.inbox.provider,
          ownerType: groupUpdate.inbox.ownerType,
          forwardingStatus: groupUpdate.inbox.forwardingStatus,
        };
      }

      const standaloneUpdate = standaloneById.get(account.connection.connectionId);
      if (standaloneUpdate) {
        account.status = standaloneUpdate.status;
        account.connection = standaloneUpdate.connection;
        account.hasPendingSubBrands = standaloneUpdate.connection.pendingSubBrands.length > 0;
      }

      if (payload.inbox && account.inbox?.id === payload.inbox.id) {
        account.inbox = {
          id: payload.inbox.id,
          email: payload.inbox.email,
          provider: payload.inbox.provider,
          ownerType: payload.inbox.ownerType,
          forwardingStatus: payload.inbox.forwardingStatus,
        };
      }
    });
  });
};

const CATALOG_REQUEST = "catalog";
const accountsRequest = (ipId: string): string => `accounts:${ipId}`;

const markRequest = (state: ConnectionHubState, key: string, requestId: string): void => {
  state.requestMarks[key] = { requestId, revision: state.socket.revision };
};

const isLatestRequest = (state: ConnectionHubState, key: string, requestId: string): boolean =>
  state.requestMarks[key]?.requestId === requestId;

const replaySocketSince = (state: ConnectionHubState, key: string): void => {
  const mark = state.requestMarks[key];
  if (mark && state.socket.latest && state.socket.revision !== mark.revision) applySocketPayload(state, state.socket.latest);
};

export const connectionHubSlice = createSlice({
  name: "connectionHub",
  initialState,
  reducers: {
    openConnectionHubSession: (
      state,
      action: PayloadAction<ConnectionHubOpenOptions>
    ) => {
      const options = action.payload;
      const agencyClientId = options.agencyClientId ?? null;
      if (state.session.agencyClientId !== agencyClientId) resetCompanyState(state);

      state.socket = { revision: state.socket.revision + 1, latest: null };
      state.session = {
        isOpen: true,
        source: options.source ?? null,
        agencyClientId,
        mode: options.mode,
        selectedIpIds: [...options.ipIds],
        activeIpId: options.ipIds[0] ?? null,
      };
    },
    closeConnectionHubSession: (state) => {
      state.session.isOpen = false;
      state.session.mode = null;
      state.session.selectedIpIds = [];
      state.session.activeIpId = null;
    },
    replaceSelectedIpIds: (state, action: PayloadAction<string[]>) => {
      state.session.selectedIpIds = [...action.payload];
    },
    setActiveIpId: (state, action: PayloadAction<string | null>) => {
      state.session.activeIpId = action.payload;
    },
    removeConnectionAccountFromCache: (
      state,
      action: PayloadAction<{ ipId: string; connectionId: string }>
    ) => {
      const { ipId, connectionId } = action.payload;
      const accounts = state.accountsByIpId[ipId];
      const removed = accounts?.find((account) => account.connection.connectionId === connectionId);
      if (!accounts || !removed) return;
      state.accountsByIpId[ipId] = accounts.filter(
        (account) => account.connection.connectionId !== connectionId
      );
      if (removed.status === CONNECTION_STATUS.INCOMPLETE) return;
      state.catalog.data?.sections.forEach((section) => {
        const channel = section.channels.find((item) => item.ipId === ipId);
        if (channel?.badge.accountCount !== undefined) {
          channel.badge.accountCount = Math.max(0, channel.badge.accountCount - 1);
        }
      });
    },
    resetEmailSuggestions: (state) => {
      state.emailSuggestions = { data: state.emailSuggestions.data, ...emptyRequestState() };
    },
    clearConnectionHubErrors: (state) => {
      state.catalog.error = null;
      state.emailSuggestions.error = null;
      Object.values(state.accountRequestsByIpId).forEach((request) => {
        request.error = null;
      });
    },
    resetConnectionHubContext: (
      state,
      action: PayloadAction<number | null>
    ) => {
      resetCompanyState(state);
      state.session.isOpen = false;
      state.session.source = null;
      state.session.agencyClientId = action.payload;
      state.session.mode = null;
      state.session.selectedIpIds = [];
      state.session.activeIpId = null;
    },
    applyConnectionSocketUpdate: (
      state,
      action: PayloadAction<ConnectionSocketPayload>
    ) => {
      state.socket.revision += 1;
      state.socket.latest = action.payload;
      applySocketPayload(state, action.payload);
    },
    markSuggestedChannels: (
      state,
      action: PayloadAction<{ agencyClientId: number | null; ipIds: string[] }>
    ) => {
      if (!sameAgency(state, action.payload.agencyClientId)) return;
      const suggested = new Set(action.payload.ipIds);
      state.catalog.data?.sections.forEach((section) => {
        section.channels.forEach((channel) => {
          channel.isSuggested = suggested.has(channel.ipId) && channel.badge.status !== CONNECTION_STATUS.CONNECTED;
        });
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectionChannels.pending, (state, action) => {
        if (!sameAgency(state, action.meta.arg?.agencyClientId)) return;
        markRequest(state, CATALOG_REQUEST, action.meta.requestId);
        state.catalog.status = REQUEST_STATUS.LOADING;
        state.catalog.error = null;
      })
      .addCase(fetchConnectionChannels.fulfilled, (state, action) => {
        if (!sameAgency(state, action.payload.agencyClientId)) return;
        if (!isLatestRequest(state, CATALOG_REQUEST, action.meta.requestId)) return;
        state.catalog.data = action.payload.data;
        replaySocketSince(state, CATALOG_REQUEST);
        state.catalog.status = REQUEST_STATUS.SUCCEEDED;
        state.catalog.error = null;
      })
      .addCase(fetchConnectionChannels.rejected, (state, action) => {
        if (!sameAgency(state, action.meta.arg?.agencyClientId)) return;
        if (!isLatestRequest(state, CATALOG_REQUEST, action.meta.requestId)) return;
        state.catalog.status = REQUEST_STATUS.FAILED;
        state.catalog.error = action.payload ?? null;
      })
      .addCase(fetchConnectionAccounts.pending, (state, action) => {
        if (!sameAgency(state, action.meta.arg.agencyClientId)) return;
        markRequest(state, accountsRequest(action.meta.arg.ipId), action.meta.requestId);
        if (action.meta.arg.silent) return;
        state.accountRequestsByIpId[action.meta.arg.ipId] = {
          status: REQUEST_STATUS.LOADING,
          error: null,
        };
      })
      .addCase(fetchConnectionAccounts.fulfilled, (state, action) => {
        if (!sameAgency(state, action.payload.agencyClientId)) return;
        const key = accountsRequest(action.payload.ipId);
        if (!isLatestRequest(state, key, action.meta.requestId)) return;
        state.accountsByIpId[action.payload.ipId] = action.payload.data;
        replaySocketSince(state, key);
        state.accountRequestsByIpId[action.payload.ipId] = {
          status: REQUEST_STATUS.SUCCEEDED,
          error: null,
        };
      })
      .addCase(fetchConnectionAccounts.rejected, (state, action) => {
        if (!sameAgency(state, action.meta.arg.agencyClientId)) return;
        if (!isLatestRequest(state, accountsRequest(action.meta.arg.ipId), action.meta.requestId)) return;
        if (action.meta.arg.silent) return;
        state.accountRequestsByIpId[action.meta.arg.ipId] = {
          status: REQUEST_STATUS.FAILED,
          error: action.payload ?? null,
        };
      })
      .addCase(fetchConnectionEmailSuggestions.pending, (state, action) => {
        if (!sameAgency(state, action.meta.arg?.agencyClientId)) return;
        state.emailSuggestions.status = REQUEST_STATUS.LOADING;
        state.emailSuggestions.error = null;
      })
      .addCase(fetchConnectionEmailSuggestions.fulfilled, (state, action) => {
        if (!sameAgency(state, action.payload.agencyClientId)) return;
        state.emailSuggestions.data = action.payload.data;
        state.emailSuggestions.status = REQUEST_STATUS.SUCCEEDED;
        state.emailSuggestions.error = null;
      })
      .addCase(fetchConnectionEmailSuggestions.rejected, (state, action) => {
        if (!sameAgency(state, action.meta.arg?.agencyClientId)) return;
        state.emailSuggestions.status = REQUEST_STATUS.FAILED;
        state.emailSuggestions.error = action.payload ?? null;
      });
  },
});

export const {
  openConnectionHubSession,
  closeConnectionHubSession,
  replaceSelectedIpIds,
  setActiveIpId,
  removeConnectionAccountFromCache,
  clearConnectionHubErrors,
  resetConnectionHubContext,
  applyConnectionSocketUpdate,
  markSuggestedChannels,
  resetEmailSuggestions,
} = connectionHubSlice.actions;

export const connectionHubReducer = connectionHubSlice.reducer;
